import React, { useState, useEffect } from "react";
import ValueCard from "./ValueCard";
import { NavLink } from "react-router-dom";
import Carousel from "../../utilities/components/Carousel";
import MapLocation from "../../utilities/components/MapLocation";
import { CONTACT_PAGE_URL } from "../../utilities/constants/PagesEndpoints";
import FacilityConstants from "../../utilities/constants/FacilityConstants";
import {
  Award,
  ShieldCheck,
  Lightbulb,
  Heart,
  Users,
  Leaf,
  PenTool,
  Phone,
  Sparkles,
  Target,
} from "lucide-react";

const AboutPage = () => {
  const [heroImageLoaded, setHeroImageLoaded] = useState(false);

  // Preload hero image
  useEffect(() => {
    const img = new Image();
    img.src = "/assets/images/about_us_cover.png";
    img.onload = () => setHeroImageLoaded(true);
  }, []);

  const values = [
    {
      name: "Excellence",
      description:
        "We pursue the highest standards in all aspects of our services, ensuring that every event is executed flawlessly.",
      icon: Award,
    },
    {
      name: "Integrity",
      description:
        "We operate with transparency and honesty, building trust with our clients and partners.",
      icon: ShieldCheck,
    },
    {
      name: "Innovation",
      description:
        "We continuously seek to innovate, embracing new trends to enhance our offerings.",
      icon: Lightbulb,
    },
    {
      name: "Hospitality",
      description:
        "We are dedicated to providing warm and gracious hospitality, ensuring that all guests feel valued.",
      icon: Heart,
    },
    {
      name: "Collaboration",
      description:
        "We work closely with our clients and partners to bring their unique visions to life.",
      icon: Users,
    },
    {
      name: "Sustainability",
      description:
        "We are committed to operating responsibly, taking measures to reduce our environmental footprint.",
      icon: Leaf,
    },
    {
      name: "Customization",
      description:
        "We personalize our services to align with our clients’ individual tastes and preferences.",
      icon: PenTool,
    },
  ];

  const images = [
    "assets/images/birthday.jpg",
    "assets/images/decoration.jpg",
    "assets/images/stage.jpg",
    "assets/images/stage_2.jpg",
    "assets/images/stage_3.jpg",
    "assets/images/tableset.jpg",
    "assets/images/tableset_1.jpg",
  ];

  return (
    <div className="bg-slate-50 dark:bg-slate-950 transition-colors duration-300">
      {/* --- Hero Section --- */}
      <section className="relative h-[60vh] min-h-[500px] flex items-center justify-center overflow-hidden">
        {/* Placeholder background color while image loads */}
        <div className="absolute inset-0 bg-slate-900 z-0" />

        {/* Hero image with fade-in effect */}
        <div
          className={`absolute inset-0 bg-cover bg-center z-0 transition-opacity duration-700 ${heroImageLoaded ? "opacity-100" : "opacity-0"}`}
          style={{
            backgroundImage: "url('/assets/images/about_us_cover.png')",
          }}
        />
        <div className="absolute inset-0 bg-black/60 z-10" />

        <div className="relative z-20 container mx-auto px-6 text-center">
          <h1 className="text-5xl md:text-7xl font-serif text-white mb-6 animate-fade-in-up">
            Step into <span className="text-yellow-500 italic">Elegance</span>
          </h1>
          <p className="text-xl md:text-2xl text-slate-200 font-light max-w-2xl mx-auto leading-relaxed animate-fade-in-up delay-100">
            Celebrate in style at Le Prestige Hall, where memories are crafted
            with magnificence.
          </p>
        </div>
      </section>

      {/* --- Our Story & Mission --- */}
      <section className="py-20 md:py-32 bg-white dark:bg-slate-950">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Icon Cards Grid */}
            <div className="grid grid-cols-2 gap-6">
              {/* Our Story Card */}
              <div className="group p-8 bg-gradient-to-br from-blue-50 to-slate-100 dark:from-slate-800 dark:to-slate-900 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 transform translate-y-8 hover:shadow-xl transition-all duration-300">
                <div className="mb-4 inline-block p-4 rounded-xl bg-blue-600 text-white shadow-lg shadow-blue-600/30">
                  <Sparkles size={36} strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2 font-serif">
                  Our Story
                </h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                  A vision born from passion for creating extraordinary
                  celebrations.
                </p>
              </div>

              {/* Our Mission Card */}
              <div className="group p-8 bg-gradient-to-br from-yellow-50 to-amber-100 dark:from-slate-800 dark:to-slate-900 rounded-2xl shadow-lg border border-slate-200 dark:border-slate-700 hover:shadow-xl transition-all duration-300">
                <div className="mb-4 inline-block p-4 rounded-xl bg-yellow-500 text-white shadow-lg shadow-yellow-500/30">
                  <Target size={36} strokeWidth={1.5} />
                </div>
                <h3 className="text-xl font-bold text-slate-900 dark:text-white mb-2 font-serif">
                  Our Mission
                </h3>
                <p className="text-slate-600 dark:text-slate-400 text-sm leading-relaxed">
                  To transform every event into an unforgettable masterpiece.
                </p>
              </div>
            </div>

            {/* Content */}
            <div className="space-y-8">
              <div>
                <span className="text-blue-600 dark:text-blue-400 font-bold tracking-wider uppercase text-sm">
                  Since 2019
                </span>
                <h2 className="text-4xl md:text-5xl font-serif font-bold text-slate-900 dark:text-white mt-2 mb-6">
                  Crafting Unforgettable Moments
                </h2>
                <p className="text-lg text-slate-600 dark:text-slate-300 leading-relaxed">
                  Welcome to Le Prestige Hall, the epitome of elegance and
                  luxury. Our journey began with a clear vision: to create an
                  unparalleled event space where special moments can be
                  celebrated and cherished.
                </p>
              </div>

              <div className="space-y-6 border-l-4 border-yellow-500 pl-6">
                <blockquote className="text-xl italic text-slate-700 dark:text-slate-200 font-serif">
                  "We aim to make every event a masterpiece that resonates with
                  elegance and excellence."
                </blockquote>
              </div>

              <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                The heart and soul of Le Prestige Hall is its team. Our
                highly-skilled personnel, with over a decade of experience, are
                committed to making each event an unforgettable experience.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* --- Core Values --- */}
      <section className="py-20 bg-slate-50 dark:bg-slate-900 border-y border-slate-200 dark:border-slate-800">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-slate-900 dark:text-white mb-4">
              Our Core Values
            </h2>
            <div className="w-24 h-1 bg-blue-600 mx-auto rounded-full" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {values.map((value, index) => (
              <ValueCard
                key={index}
                name={value.name}
                description={value.description}
                icon={value.icon}
              />
            ))}
          </div>
        </div>
      </section>

      {/* --- Location & Facilities --- */}
      <section className="py-20 bg-white dark:bg-slate-950">
        <div className="container mx-auto px-6 max-w-7xl">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-serif font-bold text-slate-900 dark:text-white mb-4">
              Visit Le Prestige Hall
            </h2>
            <p className="text-slate-600 dark:text-slate-400">
              Conveniently located with ample parking and proximity to
              high-standard hotels.
            </p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 h-[500px] lg:h-[600px]">
            <div className="rounded-2xl overflow-hidden shadow-lg h-full border border-slate-200 dark:border-slate-800">
              <MapLocation className="w-full h-full" />
            </div>
            <div className="rounded-2xl overflow-hidden shadow-lg h-full">
              <Carousel
                images={images}
                className="w-full h-full object-cover"
              />
            </div>
          </div>
        </div>
      </section>

      {/* --- Contact CTA --- */}
      <section className="py-20 bg-slate-900 text-white relative overflow-hidden">
        <div className="absolute inset-0 opacity-10 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')]"></div>
        <div className="container mx-auto px-6 max-w-4xl text-center relative z-10">
          <h2 className="text-3xl md:text-4xl font-bold font-serif mb-6">
            Ready to Plan Your Event?
          </h2>
          <p className="text-lg text-slate-300 mb-10">
            We are available from 9am to 7:30pm Monday to Saturday and 9am to
            5pm on Sunday.
          </p>

          <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
            <div className="flex items-center gap-3">
              <div className="p-3 bg-white/10 rounded-full">
                <Phone size={24} className="text-yellow-500" />
              </div>
              <div className="text-left">
                <span className="block text-xs text-slate-400 uppercase tracking-wider">
                  Call Us
                </span>
                <span className="font-bold text-lg">
                  <a
                    href={`tel:${FacilityConstants.PHONE.format4}`}
                    className="text-slate-600 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors block"
                  >
                    {FacilityConstants.PHONE.format2}
                  </a>
                </span>
              </div>
            </div>

            <div className="hidden sm:block w-px h-12 bg-white/20 mx-4"></div>

            <NavLink
              to={CONTACT_PAGE_URL}
              onClick={() =>
                globalThis.scrollTo({ top: 0, behavior: "smooth" })
              }
              className="px-8 py-4 bg-yellow-500 hover:bg-yellow-400 text-slate-900 font-bold rounded-full transition-all shadow-lg hover:shadow-yellow-500/20"
            >
              Get in Touch
            </NavLink>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
